import {
  FETCH_RFQ_NOTIFICATION_RULES_REQUEST,
  FETCH_RFQ_NOTIFICATION_RULES_SUCCESS,
  FETCH_RFQ_NOTIFICATION_RULES_FAILURE,
  FETCH_RFQ_NOTIFICATION_RULE_DETAILS_REQUEST,
  FETCH_RFQ_NOTIFICATION_RULE_DETAILS_SUCCESS,
  FETCH_RFQ_NOTIFICATION_RULE_DETAILS_FAILURE,
  CREATE_RFQ_NOTIFICATION_RULE_REQUEST,
  CREATE_RFQ_NOTIFICATION_RULE_SUCCESS,
  CREATE_RFQ_NOTIFICATION_RULE_FAILURE,
  UPDATE_RFQ_NOTIFICATION_RULE_REQUEST,
  UPDATE_RFQ_NOTIFICATION_RULE_SUCCESS,
  UPDATE_RFQ_NOTIFICATION_RULE_FAILURE,
  DELETE_RFQ_NOTIFICATION_RULE_REQUEST,
  DELETE_RFQ_NOTIFICATION_RULE_SUCCESS,
  DELETE_RFQ_NOTIFICATION_RULE_FAILURE,
  FETCH_CONTACTS_WITH_STATS_REQUEST,
  FETCH_CONTACTS_WITH_STATS_SUCCESS,
  FETCH_CONTACTS_WITH_STATS_FAILURE,
  FETCH_CONTACTS_WITH_STATS_CANCELLED,
  TEST_RFQ_NOTIFICATION_RULE_REQUEST,
  TEST_RFQ_NOTIFICATION_RULE_SUCCESS,
  TEST_RFQ_NOTIFICATION_RULE_FAILURE,
  FETCH_RFQ_SOURCES_REQUEST,
  FETCH_RFQ_SOURCES_SUCCESS,
  FETCH_RFQ_SOURCES_FAILURE,
  CLEAR_RFQ_NOTIFICATIONS_ERROR,
  CLEAR_CURRENT_RULE,
  // Schedule action types
  FETCH_SCHEDULES_REQUEST,
  FETCH_SCHEDULES_SUCCESS,
  FETCH_SCHEDULES_FAILURE,
  CREATE_SCHEDULE_REQUEST,
  CREATE_SCHEDULE_SUCCESS,
  CREATE_SCHEDULE_FAILURE,
  UPDATE_SCHEDULE_REQUEST,
  UPDATE_SCHEDULE_SUCCESS,
  UPDATE_SCHEDULE_FAILURE,
  DELETE_SCHEDULE_REQUEST,
  DELETE_SCHEDULE_SUCCESS,
  DELETE_SCHEDULE_FAILURE,
  BULK_CREATE_SCHEDULES_REQUEST,
  BULK_CREATE_SCHEDULES_SUCCESS,
  BULK_CREATE_SCHEDULES_FAILURE
} from './types';

import rfqNotificationsService from 'services/rfqNotifications.service';
import { getApiErrorMessage } from 'utils/apiErrors';

export const fetchRfqNotificationRules = (params = {}) => async (dispatch) => {
  dispatch({ type: FETCH_RFQ_NOTIFICATION_RULES_REQUEST });
  
  const { offset = 0, limit = 20 } = params;

  try {
    const data = await rfqNotificationsService.getRules({ ...params, offset, limit });
    const rules = Array.isArray(data) ? data : data?.results || [];
    const count = Array.isArray(data) ? data.length : (data?.count ?? rules.length);
    dispatch({
      type: FETCH_RFQ_NOTIFICATION_RULES_SUCCESS,
      payload: { rules, count, offset, limit },
    });
  } catch (error) {
    dispatch({
      type: FETCH_RFQ_NOTIFICATION_RULES_FAILURE,
      payload: getApiErrorMessage(error) || 'Failed to fetch notification rules'
    });
  }
};

export const createRfqNotificationRule = (ruleData) => async (dispatch) => {
  dispatch({ type: CREATE_RFQ_NOTIFICATION_RULE_REQUEST });
  
  try {
    const data = await rfqNotificationsService.createRule(ruleData);
    dispatch({
      type: CREATE_RFQ_NOTIFICATION_RULE_SUCCESS,
      payload: data
    });
  } catch (error) {
    dispatch({
      type: CREATE_RFQ_NOTIFICATION_RULE_FAILURE,
      payload: getApiErrorMessage(error) || 'Failed to create notification rule'
    });
    throw error; // Re-throw error so form can catch it
  }
};

export const updateRfqNotificationRule = (id, ruleData) => async (dispatch) => {
  dispatch({ type: UPDATE_RFQ_NOTIFICATION_RULE_REQUEST });
  
  try {
    const data = await rfqNotificationsService.updateRule(id, ruleData);
    dispatch({
      type: UPDATE_RFQ_NOTIFICATION_RULE_SUCCESS,
      payload: data
    });
  } catch (error) {
    dispatch({
      type: UPDATE_RFQ_NOTIFICATION_RULE_FAILURE,
      payload: getApiErrorMessage(error) || 'Failed to update notification rule'
    });
    throw error; // Re-throw error so form can catch it
  }
};

export const deleteRfqNotificationRule = (id) => async (dispatch) => {
  dispatch({ type: DELETE_RFQ_NOTIFICATION_RULE_REQUEST });
  
  try {
    await rfqNotificationsService.deleteRule(id);
    dispatch({
      type: DELETE_RFQ_NOTIFICATION_RULE_SUCCESS,
      payload: id
    });
  } catch (error) {
    dispatch({
      type: DELETE_RFQ_NOTIFICATION_RULE_FAILURE,
      payload: getApiErrorMessage(error) || 'Failed to delete notification rule'
    });
    throw error; // Re-throw so RulesList can display the message inside the delete dialog
  }
};

export const fetchContactsWithStats = (searchQuery = '', limit = 20, offset = 0, hasRecentActivity = true, sourceIds = [], sortField = '', sortDirection = '', signal = null) => async (dispatch) => {
  dispatch({ type: FETCH_CONTACTS_WITH_STATS_REQUEST });

  try {
    const data = await rfqNotificationsService.getContactsWithStats(searchQuery, limit, offset, hasRecentActivity, sourceIds, sortField, sortDirection, signal);
    dispatch({
      type: FETCH_CONTACTS_WITH_STATS_SUCCESS,
      payload: data
    });
  } catch (error) {
    if (error.name === 'CanceledError' || error.name === 'AbortError') {
      // Clear the loading flag — without this, an unmount-triggered
      // abort would leave contactsLoading=true in the store forever.
      dispatch({ type: FETCH_CONTACTS_WITH_STATS_CANCELLED });
      return;
    }
    dispatch({
      type: FETCH_CONTACTS_WITH_STATS_FAILURE,
      payload: getApiErrorMessage(error) || 'Failed to fetch contacts'
    });
  }
};

export const fetchSelectedContacts = (contactIds) => async (dispatch) => {
  dispatch({ type: FETCH_CONTACTS_WITH_STATS_REQUEST });
  
  try {
    const data = await rfqNotificationsService.getSelectedContacts(contactIds);
    dispatch({
      type: FETCH_CONTACTS_WITH_STATS_SUCCESS,
      payload: data
    });
    return data;
  } catch (error) {
    dispatch({
      type: FETCH_CONTACTS_WITH_STATS_FAILURE,
      payload: getApiErrorMessage(error) || 'Failed to fetch selected contacts'
    });
    throw error;
  }
};

export const testRfqNotificationRule = (ruleId) => async (dispatch) => {
  dispatch({ type: TEST_RFQ_NOTIFICATION_RULE_REQUEST });
  
  try {
    const data = await rfqNotificationsService.testRule(ruleId);
    dispatch({
      type: TEST_RFQ_NOTIFICATION_RULE_SUCCESS,
      payload: data
    });
    return data; // Return data for component use
  } catch (error) {
    dispatch({
      type: TEST_RFQ_NOTIFICATION_RULE_FAILURE,
      payload: getApiErrorMessage(error) || 'Failed to test notification rule'
    });
    throw error; // Re-throw error so component can catch it
  }
};

export const fetchRfqNotificationRuleDetails = (id) => async (dispatch) => {
  dispatch({ type: FETCH_RFQ_NOTIFICATION_RULE_DETAILS_REQUEST });
  
  try {
    const data = await rfqNotificationsService.getRuleDetails(id);
    dispatch({
      type: FETCH_RFQ_NOTIFICATION_RULE_DETAILS_SUCCESS,
      payload: data
    });
    return data;
  } catch (error) {
    dispatch({
      type: FETCH_RFQ_NOTIFICATION_RULE_DETAILS_FAILURE,
      payload: getApiErrorMessage(error) || 'Failed to fetch rule details'
    });
    throw error;
  }
};

export const fetchRfqSources = () => async (dispatch) => {
  dispatch({ type: FETCH_RFQ_SOURCES_REQUEST });
  
  try {
    const data = await rfqNotificationsService.getRfqSources();
    dispatch({
      type: FETCH_RFQ_SOURCES_SUCCESS,
      payload: data
    });
  } catch (error) {
    dispatch({
      type: FETCH_RFQ_SOURCES_FAILURE,
      payload: getApiErrorMessage(error) || 'Failed to fetch RFQ sources'
    });
  }
};

export const clearRfqNotificationsError = () => ({
  type: CLEAR_RFQ_NOTIFICATIONS_ERROR
});

export const clearCurrentRule = () => ({
  type: CLEAR_CURRENT_RULE
});

// Schedule actions
export const fetchSchedules = (criteriaId) => async (dispatch) => {
  dispatch({ type: FETCH_SCHEDULES_REQUEST });
  
  try {
    const data = await rfqNotificationsService.getSchedules(criteriaId);
    dispatch({
      type: FETCH_SCHEDULES_SUCCESS,
      payload: { criteriaId, schedules: data }
    });
    return data;
  } catch (error) {
    dispatch({
      type: FETCH_SCHEDULES_FAILURE,
      payload: getApiErrorMessage(error) || 'Failed to fetch schedules'
    });
    throw error;
  }
};

export const createSchedule = (criteriaId, scheduleData) => async (dispatch) => {
  dispatch({ type: CREATE_SCHEDULE_REQUEST });
  
  try {
    const data = await rfqNotificationsService.createSchedule(criteriaId, scheduleData);
    dispatch({
      type: CREATE_SCHEDULE_SUCCESS,
      payload: { criteriaId, schedule: data }
    });
    return data;
  } catch (error) {
    dispatch({
      type: CREATE_SCHEDULE_FAILURE,
      payload: getApiErrorMessage(error) || 'Failed to create schedule'
    });
    throw error;
  }
};

export const updateSchedule = (criteriaId, scheduleId, scheduleData) => async (dispatch) => {
  dispatch({ type: UPDATE_SCHEDULE_REQUEST });
  
  try {
    const data = await rfqNotificationsService.updateSchedule(criteriaId, scheduleId, scheduleData);
    dispatch({
      type: UPDATE_SCHEDULE_SUCCESS,
      payload: { criteriaId, schedule: data }
    });
    return data;
  } catch (error) {
    dispatch({
      type: UPDATE_SCHEDULE_FAILURE,
      payload: getApiErrorMessage(error) || 'Failed to update schedule'
    });
    throw error;
  }
};

export const deleteSchedule = (criteriaId, scheduleId) => async (dispatch) => {
  dispatch({ type: DELETE_SCHEDULE_REQUEST });
  
  try {
    await rfqNotificationsService.deleteSchedule(criteriaId, scheduleId);
    dispatch({
      type: DELETE_SCHEDULE_SUCCESS,
      payload: { criteriaId, scheduleId }
    });
  } catch (error) {
    dispatch({
      type: DELETE_SCHEDULE_FAILURE,
      payload: getApiErrorMessage(error) || 'Failed to delete schedule'
    });
    throw error;
  }
};

export const bulkCreateSchedules = (criteriaId, schedulesData) => async (dispatch) => {
  dispatch({ type: BULK_CREATE_SCHEDULES_REQUEST });
  
  try {
    const data = await rfqNotificationsService.bulkCreateSchedules(criteriaId, schedulesData);
    dispatch({
      type: BULK_CREATE_SCHEDULES_SUCCESS,
      payload: { criteriaId, schedules: data.schedules }
    });
    return data;
  } catch (error) {
    dispatch({
      type: BULK_CREATE_SCHEDULES_FAILURE,
      payload: getApiErrorMessage(error) || 'Failed to bulk create schedules'
    });
    throw error;
  }
};